import React from 'react';
import clsx from 'clsx';
import {List} from '@mui/material';
import {Alert} from '@netx/core-components/Alert';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import type {AnyUppyFile} from '../../types';
import {uploadDropzoneClasses} from '../UploadDropzone/uploadDropzoneClasses';
import {
	getUploadSingleFileUtilityClass,
	UPLOAD_SINGLE_FILE_CLASS_NAME,
	type UploadSingleFileClasses,
	uploadSingleFileClasses,
} from './uploadSingleFileClasses';
import {UploadSingleFileDropzone} from './UploadSingleFileDropzone';
import {UploadSingleFileItem} from './UploadSingleFileItem';

type UploadSingleFileOwnerState = WithClasses<UploadSingleFileClasses>;

const useUtilityClasses = (ownerState: UploadSingleFileOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getUploadSingleFileUtilityClass, classes);
};

const UploadSingleFileRoot = styled('div', {
	name: UPLOAD_SINGLE_FILE_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	flex: '1 0 auto',
	display: 'flex',
	flexDirection: 'column',
}));

const UploadSingleFileDropzoneStyled = styled(UploadSingleFileDropzone)(
	({theme}) => ({
		[`& .${uploadDropzoneClasses.splash}`]: {
			width: '100%',
			maxWidth: '100%',
			height: '100%',
			maxHeight: '100%',
		},
		[theme.breakpoints.down('sm')]: {
			[`& .${uploadDropzoneClasses.splash}`]: {
				height: '100%',
			},
		},
	}),
);

export interface UploadSingleFileProps
	extends WithClasses<UploadSingleFileClasses> {
	error?: string;
	fileLimitationText?: string;
	alertMessage?: React.ReactNode;
	alertSeverity?: 'error' | 'info' | 'success' | 'warning';
	file?: AnyUppyFile;
	progress: number;
	uploadComplete?: boolean;
	uploadStarted?: boolean;

	onAddFile?: (file: File) => void;
	onCancelFilePreview?: () => void;
	onRemoveFile?: () => void;
	onRequestFilePreview?: () => void;
}

export const UploadSingleFilePrimitive = ({
	classes: _classes,
	className,
	alertMessage,
	alertSeverity = 'info',
	error,
	fileLimitationText,
	file,
	progress,
	uploadComplete,
	uploadStarted,
	onAddFile,
	onCancelFilePreview,
	onRemoveFile,
	onRequestFilePreview,
	...props
}: UploadSingleFileProps) => {
	const ownerState = {...props, classes: _classes, className};

	const classes = useUtilityClasses(ownerState);

	return (
		<UploadSingleFileRoot className={clsx(classes.root, className)}>
			{alertMessage != null && (
				<Alert severity={alertSeverity} sx={{mb: 2}}>
					{alertMessage}
				</Alert>
			)}
			{file ? (
				<List sx={{pt: 1, pb: 2, width: '100%'}}>
					<UploadSingleFileItem
						error={file.error != null || !!error?.length}
						file={file}
						percentage={progress}
						uploadComplete={uploadComplete}
						uploadStarted={uploadStarted}
						onRemove={onRemoveFile}
						onCancelPreview={onCancelFilePreview}
						onRequestPreview={onRequestFilePreview}
					/>
				</List>
			) : (
				<UploadSingleFileDropzoneStyled
					fileLimitationText={fileLimitationText}
				/>
			)}
		</UploadSingleFileRoot>
	);
};

export const UploadSingleFile = UploadSingleFilePrimitive;
